import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { AuthShell } from "@/components/AuthShell";
import { Field, PasswordInput, PasswordRules, PrimaryButton, PW_RULES } from "@/components/FormBits";
import { Checkbox } from "@/components/ui/checkbox";

export default function SignUpPage() {
  const { register, refreshUser } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [f, setF] = useState({ email: "", password: "", accept_terms: false, confirm_age: false, marketing_opt_in: false, partner_code: (params.get("code") || "").toUpperCase() });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const pwOk = PW_RULES.every(([ok]) => ok(f.password));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!pwOk) return setError("Please choose a password that meets all requirements.");
    if (!f.confirm_age) return setError("Please confirm you are 18 or older to create an account.");
    if (!f.accept_terms) return setError("Please accept the Terms of Use and Privacy Policy to continue.");
    setBusy(true);
    try {
      const out = await register(f);
      if (out.partner) {
        if (out.partner.status === "error") toast.error(out.partner.message, { duration: 9000 });
        else toast.success(out.partner.message, { duration: 9000 });
        await refreshUser();
      }
      navigate("/onboarding");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title="Create your account" sub="For trained permanent makeup professionals and artists in training."
      footer={<>Already have an account? <Link to="/signin" data-testid="signup-signin-link" className="font-semibold text-[#2C1810] underline decoration-[#C5A059] underline-offset-4">Sign in</Link></>}>
      <form onSubmit={submit} className="space-y-5" noValidate data-testid="signup-form">
        <Field label="Email address" id="signup-email-input" type="email" autoComplete="email" required value={f.email}
          onChange={(e) => setF({ ...f, email: e.target.value })} />
        <div>
          <label htmlFor="signup-password-input" className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#6E5549]">Password</label>
          <div className="mt-1.5"><PasswordInput id="signup-password-input" autoComplete="new-password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} /></div>
          <PasswordRules password={f.password} />
        </div>
        <label className="flex cursor-pointer items-start gap-3 rounded-xl bg-[#FDF2F0] p-4">
          <Checkbox data-testid="signup-age-checkbox" checked={f.confirm_age} onCheckedChange={(v) => setF({ ...f, confirm_age: !!v })}
            className="mt-0.5 h-5 w-5 border-[#9C6218] data-[state=checked]:bg-[#2C1810]" aria-required="true" />
          <span className="text-sm leading-relaxed text-[#4A3B32]">I confirm I am 18 or older. <span className="text-[#8C2A2A]">*</span></span>
        </label>
        <label className="flex cursor-pointer items-start gap-3 rounded-xl bg-[#FDF2F0] p-4">
          <Checkbox data-testid="signup-terms-checkbox" checked={f.accept_terms} onCheckedChange={(v) => setF({ ...f, accept_terms: !!v })}
            className="mt-0.5 h-5 w-5 border-[#9C6218] data-[state=checked]:bg-[#2C1810]" aria-required="true" />
          <span className="text-sm leading-relaxed text-[#4A3B32]">
            I agree to the{" "}
            <Link to="/terms" target="_blank" data-testid="signup-terms-link" className="font-semibold underline underline-offset-2">Terms of Use</Link> and{" "}
            <Link to="/privacy" target="_blank" data-testid="signup-privacy-link" className="font-semibold underline underline-offset-2">Privacy Policy</Link>. <span className="text-[#8C2A2A]">*</span>
          </span>
        </label>
        <label className="flex cursor-pointer items-start gap-3 px-4">
          <Checkbox data-testid="signup-marketing-checkbox" checked={f.marketing_opt_in} onCheckedChange={(v) => setF({ ...f, marketing_opt_in: !!v })}
            className="mt-0.5 h-5 w-5 border-[#A88B7D] data-[state=checked]:bg-[#2C1810]" />
          <span className="text-sm leading-relaxed text-[#6E5549]">Optional: send me occasional MooreBeautyLove education updates and news.</span>
        </label>
        {error && <p role="alert" data-testid="signup-error" className="rounded-xl bg-[#F9ECEC] px-4 py-3 text-sm text-[#8C2A2A]">{error}</p>}
        <PrimaryButton type="submit" disabled={busy} data-testid="signup-submit-btn" className="w-full">{busy ? "Creating your account…" : "Create My Account"}</PrimaryButton>
      </form>
    </AuthShell>
  );
}
